import React, { useState, useCallback, useMemo } from "react";
import {
  FaInfoCircle,
  FaHandHoldingHeart,
  FaUserCheck,
  FaMoneyBillWave,
  FaExclamationTriangle,
  FaEdit,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaShieldAlt,
  FaCalendarAlt,
  FaFileContract,
  FaChevronDown,
  FaChevronRight,
  FaPrint,
} from "react-icons/fa";
import { COMPANY_INFO } from '../constants';
import Seo from './common/Seo';

// Datos estructurados de las secciones
const SECTIONS_DATA = [
  {
    id: "introduction",
    icon: FaInfoCircle,
    title: "1. Introducción",
    content: `Bienvenido a ${COMPANY_INFO.name}. Estos términos y condiciones regulan el uso de nuestros servicios de enfermería especializada en Guadalajara, Jalisco. Al contratar nuestros servicios, usted acepta cumplir con estos términos de manera integral.`,
    highlight: true
  },
  {
    id: "services",
    icon: FaHandHoldingHeart,
    title: "2. Servicios Ofrecidos",
    content: `${COMPANY_INFO.name} ofrece servicios de enfermería especializada, incluyendo:`,
    list: [
      "Atención domiciliaria para adultos mayores",
      "Cuidados postoperatorios especializados",
      "Administración de medicamentos",
      "Monitoreo de signos vitales",
      "Acompañamiento y apoyo emocional",
      "Servicios de rehabilitación básica"
    ]
  },
  {
    id: "user-obligations",
    icon: FaUserCheck,
    title: "3. Obligaciones del Cliente",
    content: "Para garantizar la calidad del servicio, el cliente se compromete a:",
    list: [
      "Proporcionar información veraz y completa sobre el estado de salud del paciente",
      "Respetar los horarios y condiciones establecidas en el contrato de servicio",
      "Cumplir con los pagos acordados en los tiempos y formas establecidos",
      "Facilitar un ambiente seguro y adecuado para la prestación del servicio",
      "Comunicar oportunamente cualquier cambio en las condiciones del paciente"
    ]
  },
  {
    id: "payments",
    icon: FaMoneyBillWave,
    title: "4. Términos de Pago y Reembolsos",
    content: "Las condiciones económicas se rigen por las siguientes políticas:",
    list: [
      "Los pagos deben realizarse puntualmente en la fecha acordada",
      "Se aceptan pagos en efectivo, transferencia bancaria o depósito",
      "No se realizan reembolsos una vez iniciado el servicio contratado",
      "En caso de cancelación anticipada, se aplicará una penalización del 20%",
      "Los precios pueden ajustarse con previo aviso de 30 días"
    ]
  },
  {
    id: "responsibility",
    icon: FaExclamationTriangle,
    title: "5. Limitación de Responsabilidad",
    content: `${COMPANY_INFO.name} se compromete a brindar servicios profesionales de la más alta calidad. Sin embargo, nuestra responsabilidad se limita a:`,
    list: [
      "Los servicios específicamente contratados y documentados",
      "El cumplimiento de protocolos médicos estándar",
      "La confidencialidad de la información del paciente"
    ],
    note: "No nos hacemos responsables por condiciones preexistentes del paciente, complicaciones médicas imprevistas, o decisiones médicas que excedan nuestro ámbito de competencia."
  },
  {
    id: "modifications",
    icon: FaEdit,
    title: "6. Modificaciones de Términos",
    content: "Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios se notificarán mediante:",
    list: [
      "Publicación en nuestro sitio web oficial",
      "Notificación directa a clientes activos",
      "Comunicación por correo electrónico",
      "Aviso con al menos 15 días de anticipación"
    ]
  }
];

// Hook personalizado para manejar el estado de las secciones colapsables
const useCollapsibleSections = (initialState = {}) => {
  const [expandedSections, setExpandedSections] = useState(initialState);

  const toggleSection = useCallback((sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  }, []);

  const expandAll = useCallback(() => {
    const allExpanded = SECTIONS_DATA.reduce((acc, section) => ({
      ...acc,
      [section.id]: true
    }), {});
    setExpandedSections(allExpanded);
  }, []);

  const collapseAll = useCallback(() => {
    setExpandedSections({});
  }, []);

  return { expandedSections, toggleSection, expandAll, collapseAll };
};

// Componente de encabezado mejorado
const TermsHeader = React.memo(() => (
  <header className="relative overflow-hidden">
    <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 rounded-2xl shadow-2xl">
      {/* Patrón de fondo decorativo */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-64 h-64 transform translate-x-32 -translate-y-32 rotate-45 bg-white rounded-full"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 transform -translate-x-24 translate-y-24 rotate-12 bg-white rounded-full"></div>
      </div>
      
      <div className="relative px-6 py-12 text-center text-white">
        <div className="flex flex-col items-center">
          <div className="bg-white/20 backdrop-blur-sm p-6 rounded-full mb-6 shadow-lg">
            <FaFileContract className="text-5xl text-white" />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
            Términos y Condiciones
          </h1>
          
          <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-6 leading-relaxed">
            {COMPANY_INFO.name} - {COMPANY_INFO.description}
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full flex items-center">
              <FaCalendarAlt className="mr-2" />
              <span>Última actualización: {COMPANY_INFO.lastUpdate}</span>
            </div>
            <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full flex items-center">
              <FaShieldAlt className="mr-2" />
              <span>Términos Vigentes</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
));

TermsHeader.displayName = 'TermsHeader';

// Componente para los controles de sección
const SectionControls = React.memo(({ onExpandAll, onCollapseAll, onPrint }) => (
  <div className="bg-white rounded-xl shadow-md p-4 mb-8 border border-gray-100">
    <div className="flex flex-wrap justify-between items-center gap-4">
      <div className="flex flex-wrap gap-2">
        <button
          onClick={onExpandAll}
          className="flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-200"
          aria-label="Expandir todas las secciones"
        >
          <FaChevronDown className="mr-2" />
          Expandir Todo
        </button>
        <button
          onClick={onCollapseAll}
          className="flex items-center px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          aria-label="Contraer todas las secciones"
        >
          <FaChevronRight className="mr-2" />
          Contraer Todo
        </button>
      </div>
      
      <div className="flex gap-2">
        <button
          onClick={onPrint}
          className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors duration-200"
          aria-label="Imprimir términos y condiciones"
        >
          <FaPrint className="mr-2" />
          Imprimir
        </button>
      </div>
    </div>
  </div>
));

SectionControls.displayName = 'SectionControls';

// Componente de sección mejorado
const Section = React.memo(({ 
  section, 
  isExpanded, 
  onToggle, 
  index 
}) => {
  const { id, icon: IconComponent, title, content, list, note, highlight } = section;
  
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle(id);
    }
  }, [id, onToggle]);

  return (
    <article className={`border-b border-gray-100 last:border-b-0 transition-all duration-300 ${highlight ? 'bg-blue-50/50' : ''}`}>
      <button
        onClick={() => onToggle(id)}
        onKeyDown={handleKeyDown}
        className="w-full p-6 md:p-8 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset transition-colors duration-200"
        aria-expanded={isExpanded}
        aria-controls={`section-content-${id}`}
        aria-describedby={`section-desc-${id}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            <div className={`p-3 rounded-xl mr-4 transition-colors duration-200 ${
              isExpanded ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-600'
            }`}>
              <IconComponent className="text-xl" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 flex-1">
              {title}
            </h2>
          </div>
          <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
            <FaChevronDown className="text-gray-400" />
          </div>
        </div>
      </button>
      
      <div
        id={`section-content-${id}`}
        className={`overflow-hidden transition-all duration-300 ${
          isExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="pl-6 pr-6 pb-6 md:pl-20 md:pr-8 md:pb-8">
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-700 leading-relaxed mb-4">{content}</p>
            
            {list && (
              <ul className="space-y-3 mt-4">
                {list.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start">
                    <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            )}
            
            {note && (
              <div className="mt-6 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg">
                <p className="text-amber-800 text-sm font-medium">
                  <strong>Nota importante:</strong> {note}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
});

Section.displayName = 'Section';

// Componente de información de contacto mejorado
const ContactSection = React.memo(() => (
  <section className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl shadow-xl text-white overflow-hidden">
    <div className="relative p-8">
      {/* Patrón decorativo */}
      <div className="absolute top-0 right-0 opacity-10">
        <div className="w-32 h-32 transform rotate-45 bg-white rounded-lg"></div>
      </div>
      
      <div className="relative">
        <div className="flex items-center mb-6">
          <div className="bg-white/20 p-3 rounded-lg mr-4">
            <FaShieldAlt className="text-2xl text-white" />
          </div>
          <h2 className="text-2xl font-bold">Información de Contacto</h2>
        </div>
        
        <p className="text-blue-100 mb-6 text-lg">
          Para dudas, consultas o aclaraciones sobre estos términos, no dude en contactarnos:
        </p>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <ContactItem
            icon={<FaEnvelope />}
            label="Email"
            value={COMPANY_INFO.email}
            href={`mailto:${COMPANY_INFO.email}`}
          />
          <ContactItem
            icon={<FaPhone />}
            label="Teléfono"
            value={COMPANY_INFO.phone}
            href={`tel:${COMPANY_INFO.phone.replace(/\s/g, '')}`}
          />
          <ContactItem
            icon={<FaMapMarkerAlt />}
            label="Dirección"
            value={COMPANY_INFO.address}
            className="md:col-span-2 lg:col-span-1"
          />
        </div>
      </div>
    </div>
  </section>
));

ContactSection.displayName = 'ContactSection';

// Componente auxiliar para items de contacto
const ContactItem = React.memo(({ icon, label, value, href, className = "" }) => (
  <div className={`bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-colors duration-200 ${className}`}>
    <div className="flex items-start space-x-3">
      <div className="text-blue-200 mt-1">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <dt className="text-sm font-medium text-blue-200 uppercase tracking-wide">
          {label}
        </dt>
        {href ? (
          <dd>
            <a 
              href={href}
              className="text-white hover:text-blue-100 transition-colors duration-200 break-words"
            >
              {value}
            </a>
          </dd>
        ) : (
          <dd className="text-white break-words">
            {value}
          </dd>
        )}
      </div>
    </div>
  </div>
));

ContactItem.displayName = 'ContactItem';

// Componente principal
const TermsAndConditions = () => {
  // Estado inicial: primera sección expandida
  const initialExpanded = useMemo(() => ({ introduction: true }), []);
  const { expandedSections, toggleSection, expandAll, collapseAll } = useCollapsibleSections(initialExpanded);

  // Función para imprimir
  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-100 to-gray-50 py-8">
      <Seo
        title="Términos y Condiciones | Geriatría Serena"
        description="Consulta los términos y condiciones de contratación de los servicios de enfermería y cuidado geriátrico a domicilio de Geriatría Serena en Guadalajara."
        canonical="https://geriatriaserena.com/terms-and-conditions"
        robots="index, follow"
      />
      <div className="max-w-5xl mx-auto px-4">
        <TermsHeader />
        
        <div className="mt-10">
          <SectionControls
            onExpandAll={expandAll}
            onCollapseAll={collapseAll}
            onPrint={handlePrint}
          />
          
          {/* Contenido Principal */}
          <main className="bg-white rounded-2xl shadow-xl overflow-hidden mb-10">
            {SECTIONS_DATA.map((section, index) => (
              <Section
                key={section.id}
                section={section}
                isExpanded={expandedSections[section.id]}
                onToggle={toggleSection}
                index={index}
              />
            ))}
          </main>
          
          <ContactSection />
        </div>
        
        {/* Footer */}
        <footer className="mt-10 text-center text-gray-500 text-sm">
          <p>© 2025 {COMPANY_INFO.name}. Todos los derechos reservados.</p>
          <p className="mt-1">Términos válidos y vigentes desde {COMPANY_INFO.lastUpdate}</p>
        </footer>
      </div>
    </div>
  );
};

export default React.memo(TermsAndConditions);